import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, FileStack, Share2, BarChart3, Timer, Download } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const FeaturesSection: React.FC = () => {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();

  const features = [
    {
      icon: Sparkles,
      title: t('landing.features.aiQuestions.title'),
      description: t('landing.features.aiQuestions.description'),
      color: 'text-primary bg-primary-50',
    },
    {
      icon: FileStack,
      title: t('landing.features.multipleSources.title'),
      description: t('landing.features.multipleSources.description'),
      color: 'text-secondary bg-secondary-50',
    },
    {
      icon: Share2,
      title: t('landing.features.instantSharing.title'),
      description: t('landing.features.instantSharing.description'),
      color: 'text-accent bg-accent-50',
    },
    {
      icon: BarChart3,
      title: t('landing.features.realTimeScoring.title'),
      description: t('landing.features.realTimeScoring.description'),
      color: 'text-primary bg-primary-50',
    },
    {
      icon: Timer,
      title: t('landing.features.timedQuestions.title'),
      description: t('landing.features.timedQuestions.description'),
      color: 'text-secondary bg-secondary-50',
    },
    {
      icon: Download,
      title: t('landing.features.csvExport.title'),
      description: t('landing.features.csvExport.description'),
      color: 'text-accent bg-accent-50',
    },
  ];

  return (
    <section id="features" className="py-20 bg-white">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
            {t('landing.features.title')}{' '}
            <span className="gradient-text">{t('landing.features.titleHighlight')}</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t('landing.features.subtitle')}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className={`group p-6 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-lg hover:shadow-primary/5 transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${f.color} group-hover:scale-110 transition-transform`}>
                  <Icon size={24} />
                </div>
                <h3 className="font-heading font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
